import moment from 'moment';
import { isQuarter } from '@domain/_screens/overview/segmented/graph/segmented.utils';
import { API_DATE_FORMAT, DATE_FORMAT_BY_SEGMENTATION, getMomentFromDate } from '@shared/utils/date';
import { METRICS_TYPES, PERCENTAGE_METRICS } from '@shared/constants/MetricsTypeConstants';
import {
    formatDateBySegmentation,
} from '@shared/components/tooltips/segmented-tooltip/segmented-tooltip.utils';
import { METRIC_SEGMENTATION_MAPPING, SEGMENTATIONS } from '../constants';

const MONTH_ORDER_FORMAT = 'YYYY-MM';
const getXAxisTransformation = (segmentation) => {
    let transform = total => getMomentFromDate(total)
        .format(API_DATE_FORMAT);

    if (segmentation === SEGMENTATIONS.HOUR) {
        return total => Number.parseInt(total, 10);
    }

    if (segmentation === SEGMENTATIONS.MONTH) {
        transform = total => getMomentFromDate(total)
            .format(MONTH_ORDER_FORMAT);
    }

    return transform;
};

function getMetricForSegmentation(metricsObject, segmentation) {
    return metricsObject[METRIC_SEGMENTATION_MAPPING[segmentation]];
}

const orderBySegmentation = (total, segmentation) => (total ? total.sort((firstElement,
    secondElement) => {
    const xAxisTransformation = getXAxisTransformation(segmentation);
    const segmentationDateFirstElement = getMetricForSegmentation(firstElement, segmentation);
    const segmentationDateSecondElement = getMetricForSegmentation(secondElement, segmentation);

    if (segmentation === SEGMENTATIONS.HOUR) {
        return xAxisTransformation(segmentationDateFirstElement)
                - xAxisTransformation(segmentationDateSecondElement);
    }
    return xAxisTransformation(segmentationDateFirstElement)
        .localeCompare(xAxisTransformation(segmentationDateSecondElement));
}) : []);

export const getMetricData = (total, metric, segmentation) => {
    const totalOrderedBySegmentation = orderBySegmentation(total, segmentation);

    return totalOrderedBySegmentation.map((x) => {
        const metricValue = metric?.metric?.value;
        if (!metricValue) {
            return Number(0).toFixed(2);
        }
        const isPercentage = PERCENTAGE_METRICS.includes(metricValue);
        const multiplier = isPercentage ? 100 : 1;

        return (parseFloat(x[metricValue]) * multiplier).toFixed(2);
    });
};

export const getDataForSecondMetric = (total, totalForComparison, firstMetric, secondMetric) => {
    const isPeriodComparisonSelected = secondMetric?.metric?.type === METRICS_TYPES.comparison;
    return isPeriodComparisonSelected ? getMetricData(totalForComparison, firstMetric)
        : getMetricData(total, secondMetric);
};

const getCorrectDateForSegmentation = (date, segmentation) => {
    const segmentationFormat = DATE_FORMAT_BY_SEGMENTATION[segmentation];
    if (segmentation === SEGMENTATIONS.MONTH) {
        return date.startOf('month')
            .format(segmentationFormat);
    }

    if (segmentation === SEGMENTATIONS.WEEK) {
        return date.startOf('isoWeek')
            .format(segmentationFormat);
    }

    if (segmentation === SEGMENTATIONS.QUARTER) {
        return date.startOf('quarter')
            .format(segmentationFormat);
    }

    return date.format(segmentationFormat);
};

export function getHotspotData(hotspots, total, segmentation) {
    if (segmentation === SEGMENTATIONS.HOUR) {
        return [];
    }

    return hotspots
        .map(x => ([
            getCorrectDateForSegmentation(moment(x.start_date), segmentation),
            0,
            x,
        ]));
}

export const xAxisFormatter = segmentation => (date) => {
    const formatsOptions = {
        [SEGMENTATIONS.HOUR]: `${date}h`,
        [SEGMENTATIONS.QUARTER]: date,
    };

    const formattedDate = formatsOptions[segmentation]
        || formatDateBySegmentation(date, segmentation);

    return formattedDate;
};

export const getXAxisData = (total, segmentation, totalForComparison, isComparison) => {
    if (isComparison) {
        return segmentation === SEGMENTATIONS.HOUR ? total.map(metric => metric.hourofday)
            : total.map(() => '');
    }

    const orderedData = orderBySegmentation(total, segmentation);
    if (segmentation === SEGMENTATIONS.HOUR) {
        return orderedData.map(x => getMetricForSegmentation(x, segmentation));
    }

    return !isQuarter(segmentation)
        ? orderedData.map(x => getMomentFromDate(getMetricForSegmentation(x, segmentation)))
            .map(x => x.format(DATE_FORMAT_BY_SEGMENTATION[segmentation]))
        : orderedData.map(x => getMetricForSegmentation(x, segmentation));
};
